/**
 * Date and Time Utility Helper for QR Redirect Automation UI
 * 
 * This module handles timezone conversions between the user's local timezone
 * and UTC for the backend. It ensures that:
 * 1. User always works in their local timezone
 * 2. Backend receives/stores everything in UTC
 * 3. Status calculations are based on the user's local time
 */

export type RuleStatusEnum = 'all' | 'submitted' | 'draft' | 'scheduled' | 'active' | 'inactive';

/**
 * Convert user's local datetime to UTC ISO string for backend
 * 
 * @param localDateTimeString - Date string in format "YYYY-MM-DD HH:mm" (user's local time)
 * @returns ISO 8601 UTC string (e.g., "2026-02-06T13:00:00.000Z")
 * 
 * @example
 * // User in IST (UTC+5:30) enters "2026-02-06 18:30"
 * convertLocalToUTC("2026-02-06 18:30")
 * // Returns: "2026-02-06T13:00:00.000Z" (18:30 IST = 13:00 UTC)
 */
export function convertLocalToUTC(localDateTimeString: string): string {
  if (!localDateTimeString || localDateTimeString.trim() === '') {
    throw new Error('DateTime is required');
  }

  // Create Date object - browser automatically interprets this in user's timezone
  const localDate = new Date(localDateTimeString);
  
  if (isNaN(localDate.getTime())) {
    throw new Error('Invalid date/time format');
  }

  // Convert to UTC ISO string
  return localDate.toISOString();
}

/**
 * Format UTC datetime from backend to user's local timezone with readable format
 * 
 * @param utcString - UTC ISO string from backend (e.g., "2026-02-06T13:00:00.000Z")
 * @param options - Optional Intl.DateTimeFormatOptions to customize formatting
 * @returns Formatted datetime string in user's timezone
 * 
 * @example
 * // User in IST viewing UTC time
 * formatToLocalTime("2026-02-06T13:00:00.000Z")
 * // Returns: "Feb 6, 2026, 06:30 PM IST"
 */
export function formatToLocalTime(
  utcString: string | undefined | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!utcString) return 'N/A';

  const date = new Date(utcString);
  
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  // Get the date parts without timezone first
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };

  const datePart = date.toLocaleString('en-US', dateOptions);
  
  // Get timezone abbreviation
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Map common timezones to abbreviations
  const timezoneMap: Record<string, string> = {
    'Asia/Kolkata': 'IST',
    'Asia/Calcutta': 'IST',
    'America/New_York': 'EST',
    'America/Los_Angeles': 'PST',
    'America/Chicago': 'CST',
    'America/Denver': 'MST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Australia/Sydney': 'AEDT'
  };
  
  const abbreviation = timezoneMap[timezone] || date.toLocaleTimeString('en-US', { 
    timeZoneName: 'short' 
  }).split(' ').pop() || 'UTC';
  
  return `${datePart} ${abbreviation}`;
}

/**
 * Format UTC datetime in a compact format (DD/MM/YYYY HH:mm AM/PM TZ)
 * 
 * @param utcString - UTC ISO string from backend
 * @returns Compact formatted datetime string
 * 
 * @example
 * formatToLocalTimeCompact("2026-02-06T13:00:00.000Z")
 * // Returns: "06/02/2026 06:30 PM IST"
 */
export function formatToLocalTimeCompact(utcString: string | undefined | null): string {
  if (!utcString) return 'N/A';

  const date = new Date(utcString);
  
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  // Get timezone abbreviation
  const timeZoneName = date.toLocaleTimeString('en-US', { 
    timeZoneName: 'short' 
  }).split(' ').pop();

  // Format: DD/MM/YYYY
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  // Format time: HH:mm AM/PM
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;

  return `${formattedDate} ${formattedTime} ${timeZoneName}`;
}

/**
 * Calculate rule status based on current time and rule dates
 * This is the KEY function that determines status based on user's local time
 * 
 * @param rule - Rule object with ruleStartFrom and ruleEndAt fields
 * @returns 'active' | 'scheduled' | 'inactive'
 * 
 * @example
 * // Current time: 7:00 PM IST (Feb 6, 2026)
 * // Rule start: 6:24 PM IST (Feb 6, 2026) = "2026-02-06T12:54:00.000Z" UTC
 * calculateRuleStatus({ ruleStartFrom: "2026-02-06T12:54:00.000Z" })
 * // Returns: 'active' (because current time > start time)
 * 
 * @example
 * // Current time: 2:00 PM IST (Feb 6, 2026)
 * // Rule start: 6:24 PM IST (Feb 6, 2026) = "2026-02-06T12:54:00.000Z" UTC
 * calculateRuleStatus({ ruleStartFrom: "2026-02-06T12:54:00.000Z" })
 * // Returns: 'scheduled' (because current time < start time)
 */
export function calculateRuleStatus(rule: {
  ruleStartFrom?: string | null;
  ruleEndAt?: string | null;
}): RuleStatusEnum {
  // Get current time in user's timezone (as Date object)
  const now = new Date();
  
  // Convert UTC strings to Date objects (automatically converted to user's timezone)
  const startTime = rule.ruleStartFrom ? new Date(rule.ruleStartFrom) : null;
  const endTime = rule.ruleEndAt ? new Date(rule.ruleEndAt) : null;

  // Check if rule has ended
  if (endTime && now >= endTime) {
    return 'inactive' as RuleStatusEnum;
  }

  // Check if rule should be active
  // If no start time, or start time is in the past/now
  if (!startTime || now >= startTime) {
    return 'active' as RuleStatusEnum;
  }

  // Rule is scheduled for future
  return 'scheduled' as RuleStatusEnum;
}

/**
 * Get status badge color based on status
 * 
 * @param status - Rule status
 * @returns Color hex code
 */
export function getStatusColor(status: RuleStatusEnum | string): string {
  switch (status?.toLowerCase()) {
    case 'active':
      return '#4caf50'; // Green
    case 'scheduled':
      return '#ff9800'; // Orange
    case 'inactive':
      return '#9e9e9e'; // Gray
    case 'submitted':
      return '#2196f3'; // Blue
    default:
      return '#9e9e9e'; // Gray
  }
}

/**
 * Get status badge background color (lighter shade)
 * 
 * @param status - Rule status
 * @returns Background color hex code
 */
export function getStatusBackgroundColor(status: RuleStatusEnum | string): string {
  switch (status?.toLowerCase()) {
    case 'active':
      return '#e8f5e9'; // Light green
    case 'scheduled':
      return '#fff3e0'; // Light orange
    case 'inactive':
      return '#f5f5f5'; // Light gray
    case 'submitted':
      return '#e3f2fd'; // Light blue
    default:
      return '#f5f5f5'; // Light gray
  }
}

/**
 * Get user's current timezone
 * 
 * @returns IANA timezone string (e.g., "Asia/Kolkata", "America/New_York")
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get timezone abbreviation (e.g., "IST", "PST", "GMT")
 * 
 * @returns Timezone abbreviation
 */
export function getTimezoneAbbreviation(): string {
  const date = new Date();
  const timeZoneName = date.toLocaleTimeString('en-US', { 
    timeZoneName: 'short' 
  }).split(' ').pop();
  
  return timeZoneName || 'UTC';
}

/**
 * Convert separate date and time inputs to UTC ISO string
 * Used when user fills separate date and time fields
 * 
 * @param date - Date string (YYYY-MM-DD)
 * @param time - Time string (HH:mm)
 * @returns UTC ISO string or undefined if inputs are invalid
 * 
 * @example
 * // User in IST enters date "2026-02-06" and time "18:30"
 * combineDateTimeToUTC("2026-02-06", "18:30")
 * // Returns: "2026-02-06T13:00:00.000Z"
 */
export function combineDateTimeToUTC(
  date: string | undefined, 
  time: string | undefined
): string | undefined {
  if (!date || !time) return undefined;
  
  try {
    const combined = `${date}T${time}:00`;
    return convertLocalToUTC(combined);
  } catch (error) {
    console.error('Error combining date/time:', error);
    return undefined;
  }
}

/**
 * Parse UTC ISO string to separate date and time components in user's local timezone
 * Useful for populating date/time input fields when editing a rule
 * 
 * @param utcString - UTC ISO string from backend
 * @returns Object with date (YYYY-MM-DD) and time (HH:mm) in local timezone
 * 
 * @example
 * // User in IST viewing UTC time "2026-02-06T13:00:00.000Z"
 * parseUTCToLocalComponents("2026-02-06T13:00:00.000Z")
 * // Returns: { date: "2026-02-06", time: "18:30" }
 */
export function parseUTCToLocalComponents(utcString: string | undefined | null): {
  date: string;
  time: string;
} | null {
  if (!utcString) return null;

  const date = new Date(utcString);
  
  if (isNaN(date.getTime())) {
    return null;
  }

  // Extract date in local timezone
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;

  // Extract time in local timezone
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeString = `${hours}:${minutes}`;

  return {
    date: dateString,
    time: timeString
  };
}

/**
 * Check if a date/time is in the past
 * 
 * @param utcString - UTC ISO string
 * @returns true if the datetime is in the past
 */
export function isInPast(utcString: string | undefined | null): boolean {
  if (!utcString) return false;
  
  const date = new Date(utcString);
  const now = new Date();
  
  return date < now;
}

/**
 * Check if a date/time is in the future
 * 
 * @param utcString - UTC ISO string
 * @returns true if the datetime is in the future
 */
export function isInFuture(utcString: string | undefined | null): boolean {
  if (!utcString) return false;
  
  const date = new Date(utcString);
  const now = new Date();
  
  return date > now;
}

/**
 * Calculate duration between two dates in days
 * 
 * @param startUTC - Start datetime UTC ISO string
 * @param endUTC - End datetime UTC ISO string
 * @returns Number of days (rounded) or null if invalid
 */
export function calculateDurationInDays(
  startUTC: string | undefined | null,
  endUTC: string | undefined | null
): number | null {
  if (!startUTC || !endUTC) return null;

  const start = new Date(startUTC);
  const end = new Date(endUTC);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return null;
  }

  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return diffDays;
}
